import type { AlertColor } from '@mui/material'
import { Alert, Box, Snackbar } from '@mui/material'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { v4 as uuidV4 } from 'uuid'

import CustomTypography from '@/components/dataDisplay/CustomTypography'

export type AlertParams = {
  uuid: string
  message: string | ReactNode
  title?: string
  severity: AlertColor
  isPersistent?: boolean
  action?: ReactNode
}

type AlertContextType = {
  pushAlert: (params: Omit<AlertParams, 'uuid'>) => void
  resetAlerts: () => void
}

const AUTO_HIDE_DURATION = 6000
// Maximum number of alerts that can be displayed at the same time.
const MAX_VISIBLE_ALERTS = 3

export const AlertContext = createContext<AlertContextType>({ pushAlert: () => {}, resetAlerts: () => {} })

export const AlertProvider = ({ children }) => {
  const [open, setOpen] = useState(false)
  const [alerts, setAlerts] = useState<AlertParams[]>([])
  const [persistentAlert, setPersistentAlert] = useState<AlertParams | null>(null)
  const [isReset, setIsReset] = useState(false)

  const pushAlert = useCallback((newAlert: Omit<AlertParams, 'uuid'>) => {
    if (newAlert.isPersistent) {
      setPersistentAlert({ ...newAlert, uuid: uuidV4() })
      return
    }

    setAlerts((prev) => [...prev, { ...newAlert, uuid: uuidV4() }])
  }, [])

  const resetAlerts = useCallback(() => {
    // Use functional update to check current alerts without needing it in dependencies
    setAlerts((prevAlerts) => {
      if (prevAlerts.length > 0) {
        setIsReset(true)
        setOpen(false)
      }
      return prevAlerts
    })
    // Always set to null regardless of current value to avoid stale closure
    setPersistentAlert(null)
  }, [])

  const state = useMemo(() => ({ pushAlert, resetAlerts }), [pushAlert, resetAlerts])

  useEffect(() => {
    setOpen(alerts.length > 0 || persistentAlert !== null)
  }, [alerts, persistentAlert])

  function popAlert() {
    if (isReset) {
      setAlerts([])
      setIsReset(false)
    } else {
      setAlerts((prevState) => prevState.slice(1))
    }
  }

  function handleSnackbarClose() {
    setAlerts([])
    setIsReset(false)
  }

  return (
    <AlertContext.Provider value={state}>
      {children}

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={AUTO_HIDE_DURATION}
        open={open}
        onClose={persistentAlert !== null ? undefined : handleSnackbarClose}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          {persistentAlert && (
            <Alert
              action={persistentAlert.action}
              elevation={6}
              severity={persistentAlert.severity}
              variant="filled"
              onClose={() => setPersistentAlert(null)}
            >
              {persistentAlert.title && <CustomTypography variant="h3">{persistentAlert.title}</CustomTypography>}
              {typeof persistentAlert.message === 'string' ? (
                <CustomTypography sx={{ whiteSpace: 'pre-line' }} variant="body1">
                  {persistentAlert.message}
                </CustomTypography>
              ) : (
                persistentAlert.message
              )}
            </Alert>
          )}

          {alerts.slice(0, MAX_VISIBLE_ALERTS).map((alert) => (
            <Alert
              key={alert.uuid}
              action={alert.action}
              elevation={6}
              severity={alert.severity}
              variant="filled"
              onClose={popAlert}
            >
              {alert?.title && <CustomTypography variant="h3">{alert.title}</CustomTypography>}
              {typeof alert.message === 'string' ? (
                <CustomTypography sx={{ whiteSpace: 'pre-line' }} variant="body1">
                  {alert.message}
                </CustomTypography>
              ) : (
                alert.message
              )}
            </Alert>
          ))}
        </Box>
      </Snackbar>
    </AlertContext.Provider>
  )
}

export function useAlertContext() {
  return useContext(AlertContext)
}
